import { updateHeader } from 'common/services/common.service';
import { useEffect, useState } from 'react';
import { TABSET } from './config';
import { withTransaction } from '@elastic/apm-rum-react';
import { useHistory } from 'react-router-dom';
import { Links } from 'common/components/links/Links';
import { SubTabsLinks } from 'common/components/SubTabsLinks/SubTabsLinks';
import { Grid } from 'common/components/grid/Grid';
import { AppDispatch, IReduxState } from 'app/store';
import { useDispatch, useSelector } from 'react-redux';
import { ColumnsManage } from 'common/components/grid/columnsManage';
import {
  removeAllRows,
  resetQueryString,
  setFields,
  setInitialPath,
  getRows
} from 'features/GridSlice';
import { setColumnPicker, setShowColumnPicker } from 'features/columnPickerSlice';
import keyDomainsColumns from './SeedData/KeyDomains/columns.json';
import relatedNamesColumns from './SeedData/RelatedNames/columns.json';
import { AssetManagementActions } from './AssetManagementActions';
import { PopUp } from 'common/components/popUp/PopUp';
import { KeyDomainsDeletePopup } from './KeyDomainsDeletePopup/KeyDomainsDeletePopup';
import { of } from 'rxjs';

import './AssetManagement.scss';

function AssetManagement(props: any) {
  const dispatch: AppDispatch = useDispatch();
  const gridState = useSelector((state: IReduxState) => state.grid);
  const [tab, setTab] = useState<string>(props.match?.params?.tab || 'seed-data');
  const [subTab, setSubTab] = useState<string | undefined>(props.match?.params?.subtab || 'key-domains');
  const [metaData] = useState<any>({
    'seed-data': { count: 0 },
  });
  const [columns, setColumns] = useState<any[]>([]);
  const [identifier, setIdentifier] = useState<string>('');
  const [isCSVDownloadable, setIsCSVDownloadable] = useState<boolean>(false);
  const [rootPath, setRootPath] = useState<string>('');
  const [showDeletePopup, setShowDeletePopup] = useState<boolean>(false);
  const [deletePopupData, setDeletePopupData] = useState<{
    assets: string[];
    action: 'delete-and-blacklist' | 'delete-and-retain' | 'delete-related-names';
  }>({ assets: [], action: 'delete-and-blacklist' });
  const history = useHistory();

  const handleDeletePopup = (assets: string[], action: 'delete-and-blacklist' | 'delete-and-retain' | 'delete-related-names') => {
    setDeletePopupData({ assets, action });
    setShowDeletePopup(true);
  };

  const handlePopupClose = () => {
    setShowDeletePopup(false);
  };

  const loadGridData = (columnsConfig: any, apiPath: string, gridType: string) => {
    dispatch(removeAllRows());
    const columnsRes = of(columnsConfig);

    columnsRes.subscribe((columns: any) => {
      dispatch(setInitialPath(apiPath));
      let fields = ColumnsManage.checkGridColumnsToDisplay(columns.data);
      dispatch(setFields(fields));
      dispatch(setColumnPicker(columns.data));
      dispatch(setShowColumnPicker(columns.is_displayable));
      dispatch(getRows());

      setColumns(ColumnsManage.createColumns(
        columns.identifier_field,
        columns.data || [],
        {
          page: 'settings',
          category: 'asset-management',
          tab: gridType === 'KeyDomains' ? 'key-domains' : 'related-names'
        },
        {
          onDeletePopup: handleDeletePopup
        }
      ));
      setIdentifier(columns.identifier_field);
      setIsCSVDownloadable(columns.is_csv_downloadable);
      setRootPath(apiPath);
    });
  };

  const getGridData = () => {
    loadGridData(
      keyDomainsColumns,
      'settings/asset-management/seed-data/key-domains/',
      'KeyDomains'
    );
  };

  const getRelatedNamesGridData = () => {
    loadGridData(
      relatedNamesColumns,
      'settings/asset-management/seed-data/related-names/',
      'RelatedNames'
    );
  };

  useEffect(() => {
    const newTab = props.match?.params?.tab || 'seed-data';
    setTab(newTab);
    const _subTab = props.match?.params?.subtab || (newTab === 'seed-data' ? 'key-domains' : undefined);
    setSubTab(_subTab);
    if (!props.match?.params?.tab && window.location.pathname === '/pages/settings/asset-management') {
      history.replace('/pages/settings/asset-management/seed-data/key-domains');
    }
  }, [props.match?.params?.tab, props.match?.params?.subtab, history]);

  useEffect(() => {
    updateHeader.sendHeaderText('Settings / Asset Management');
    dispatch(removeAllRows());

    setColumns([]);
    setIdentifier('');
    setIsCSVDownloadable(false);
    setRootPath('');

    const timeoutId = setTimeout(() => {
      if (tab === 'seed-data' && subTab === 'key-domains') {
        getGridData();
      }
      if (tab === 'seed-data' && subTab === 'related-names') {
        getRelatedNamesGridData();
      }
    }, 0);

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, subTab]);

  useEffect(() => {
    dispatch(resetQueryString());
    return () => {
      dispatch(removeAllRows());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  const renderTabContent = (gridState: any) => {
    if (tab === 'seed-data') {
      if (subTab === 'key-domains' || subTab === 'related-names') {
        if (columns.length > 0) {
          return (
            <div style={{ height: '100%', zIndex: 1 }}>
              <Grid
                columns={columns}
                rows={gridState.rows}
                count={gridState.count}
                path={rootPath}
                isMoreButton={false}
                isCSVDownloadable={isCSVDownloadable}
                identifier={identifier}
                initialState={{
                  pinnedColumns: { right: ['actions'] }
                }}
                addedActions={(props: any) => (
                  <AssetManagementActions
                    ids={props.ids || []}
                    tab={tab}
                    subTab={subTab || ''}
                    identifier={identifier}
                  />
                )}
                clientSideMode={true}
              />
            </div>
          );
        } else {
          return <div className="grid-loading">Loading {subTab === 'key-domains' ? 'Key Domains' : 'Related Names'} grid...</div>;
        }
      }
    }

    return (
      <div className="grid-loading">Loading Asset Management...</div>
    );
  };

  return (
    <div className={`sub-layout-pages main-content-area ${tab}`}>
      <div>
        <Links
          page="AssetManagement"
          selectTab={tab}
          tabs={TABSET}
          metaData={metaData}
          hideCounts={true}
          hideGroupFilter={true}
        />
        {subTab && TABSET[tab]?.subTabs && <SubTabsLinks tabs={TABSET[tab].subTabs} activeSubTab={subTab} />}
      </div>
      {renderTabContent(gridState)}

      {(subTab === 'key-domains' || subTab === 'related-names') && (
        <PopUp visible={showDeletePopup} setVisible={handlePopupClose}>
          <KeyDomainsDeletePopup
            onClose={handlePopupClose}
            assets={deletePopupData.assets}
            action={deletePopupData.action}
          />
        </PopUp>
      )}
    </div>
  );
}

export default withTransaction('AssetManagement', 'page-load')(AssetManagement);
